import {useLocation, useNavigate} from "react-router-dom";
import {useCallback, useEffect, useState} from "react";
import SystemLeaveFlashDialog from "../../components/dialogs/SystemLeaveFlashDialog";
import FlashQuestionDontKnowDialog from "../../components/dialogs/courses/flash/FlashQuestionDontKnowDialog";
import {kInternalRoutes} from "../../constants/internalRoutes";
import {isArrayNullUndefinedOrEmpty, isNullOrUndefined} from "../../services/Validator";
import {Environment} from "../../enums/Environment";
import {kFakeSingleLevelA} from "../../services/Faker";
import {kEmptyResponseError, kFlashQuestionsError, kLevelAError} from "../../constants/errors";
import {sendErrorToSentry} from "../../services/Sentry";
import {get_flash_from_levelA_id} from "../../api/question";
import SystemAppContentError from "../../components/errors/SystemAppContentError";
import {get_nested} from "../../services/JsonHandler";
import NeoFlashCoreContent from "../../components/others/flashScreen/NeoFlashCoreContent";
import NeoFlashBottomBar from "../../components/bars/flashScreen/NeoFlashBottomBar";
import NeoFlashTopBar from "../../components/bars/flashScreen/NeoFlashTopBar";
import {Helmet} from "react-helmet";
import {buildHelmetTitle} from "../../helpers/SeoHelper";
import NeoFlashProgressBar from "../../components/bars/flashScreen/NeoFlashProgressBar";
import SystemAppContentLoader from "../../components/loaders/SystemAppContentLoader";
import NeoFlashResultsScreen from "./NeoFlashResultsScreen";

export default function NeoMetaFlashScreen() {
    // Used through the app to manage a Flash from questions
    // to results screens

    // PROPERTIES
    // ---- Navigation
    const location = useLocation()
    const origin = location.state?.origin
    const levelAId = location.state?.levelAId
    const navigate = useNavigate()
    // ---- Api
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)
    const [flash, setFlash] = useState(null)
    // ---- State
    const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false)
    const [openDontKnowDialog, setOpenDontKnowDialog] = useState(false)
    const [questionIndex, setQuestionIndex] = useState(0)
    const currentQuestionIndex = questionIndex + 1
    const [selectedOption, setSelectedOption] = useState(null)
    const [isFlashOver, setIsFlashOver] = useState(false)

    // HOOKS
    // ---- Memorizes a function, preventing it from being redefined on every render, unless its dependencies change.
    const getFlashBasedOnLevelAId = useCallback(() => {
        // <<<< For .env.faker ----
        if (process.env.REACT_APP_ENV === Environment.FAKER) {
            setFlash(kFakeSingleLevelA)
            setIsLoading(false)
            setError(null)
            return
        }
        // ---- For .env.faker >>>>

        if (isNullOrUndefined(levelAId))
            return
        // Switch loader on and clear error
        setIsLoading(true)
        setError(null)
        // Perform
        get_flash_from_levelA_id(levelAId)
            .then((res) => {
                if (isNullOrUndefined(res) || isNullOrUndefined(res?.levelA) || isArrayNullUndefinedOrEmpty(res?.questions)) {
                    setError(kEmptyResponseError)
                    sendErrorToSentry(kEmptyResponseError.sentry, "fetching flash from NeoMetaFlashScreen")
                } else {
                    setFlash(res)
                }
            }).catch((err) => {
            console.error(err)
            setError(kLevelAError)
            sendErrorToSentry(kFlashQuestionsError.sentry, "NeoMetaFlashScreen")
        }).finally(() => {
            setIsLoading(false)
        })
    }, [levelAId])
    // ---- At rendering : block straight access, redirect to CoursesScreen
    // ---- OR fetch Flash object from api
    useEffect(() => {
        if (isNullOrUndefined(levelAId)) {
            navigate(kInternalRoutes.courses)
        } else {
            getFlashBasedOnLevelAId()
        }
    }, [getFlashBasedOnLevelAId, levelAId, navigate]);

    // BUILD
    if (!isNullOrUndefined(error))
        return <SystemAppContentError error={error}
                                      isLoading={isLoading}
                                      retryCallback={() => getFlashBasedOnLevelAId()} />
    if (isLoading)
        return <SystemAppContentLoader />
    if (isFlashOver)
        return <NeoFlashResultsScreen levelA={flash.levelA}
                                      questions={flash?.questions}
                                      isLeaveModalOpen={isLeaveModalOpen}
                                      setIsLeaveModalOpen={setIsLeaveModalOpen}
                                      leaveFlash={leaveFlashSessionCallback} />
    return (
        <div className="overflow-auto animate-appear w-full h-[100svh] bg-backgroundLight3 flex flex-col items-center justify-start">
            {/** META */}
            <Helmet>
                <title>{buildHelmetTitle('Ummia - Flash')}</title>
                <meta name="description"
                      content="Maitrise définitivement chaque savoir-faire fondamental" />
            </Helmet>
            {/** MODAL */}
            <SystemLeaveFlashDialog open={isLeaveModalOpen}
                                    handleClose={processLeaveFlashDialogClosing}
                                    leave={leaveFlashSessionCallback} />
            <FlashQuestionDontKnowDialog open={openDontKnowDialog}
                                         handleClose={() => setOpenDontKnowDialog(false)} />
            {/** TOP BAR */}
            <NeoFlashTopBar levelA={flash.levelA}
                            onButtonClick={onLeaveFlashPressed} />
            <NeoFlashProgressBar currentQuestion={currentQuestionIndex}
                                 totalQuestion={flash?.questions?.length} />
            {/** CONTENT */}
            <div className={"mt-[48px] w-full max-w-[1024px] flex flex-col justify-start items-center px-[48px]"}>
                <NeoFlashCoreContent question={flash?.questions[questionIndex]}
                                      optionSelected={selectedOption}
                                      setOptionSelected={setSelectedOption}
                                      setOpenDontKnowDialog={setOpenDontKnowDialog} />
                <NeoFlashBottomBar selectedOption={selectedOption}
                                   submitAnswer={submitFlashQuestionAnswer} />
            </div>
        </div>
    );

    // METHODS
    // ---- LOGIC : Save answer & eventually go to correction
    function submitFlashQuestionAnswer() {
        // Destructuring question and adding selected answer
        flash.questions[questionIndex] = {...flash.questions[questionIndex], selectedOption: selectedOption}
        // Emptying selected option var
        setSelectedOption(null)
        // Making flash go forward : next question or correction depending on index
        if (questionIndex === flash.questions.length - 1) {
            // Case : last question
            // ---- Update state
            setIsFlashOver(true)
        } else {
            // Case : not last question
            setQuestionIndex(questionIndex + 1)
        }
    }

    // ---- NAV : On leave flash pressed
    function onLeaveFlashPressed() {
        if (!isLeaveModalOpen) {
            setIsLeaveModalOpen(true)
        }
    }

    // ---- NAV : Close leave flash modal
    function processLeaveFlashDialogClosing() {
        if (isLeaveModalOpen) {
            setIsLeaveModalOpen(false)
        }
    }

    // ---- LOGIC : Navigate to previous screen
    function leaveFlashSessionCallback() {
        let slug = get_nested(['levelA', 'slug'], flash, null)
        if (isNullOrUndefined(slug)) {
            navigate(kInternalRoutes.courses)
        } else {
            navigate(`/savoir-faire/${flash.levelA.slug}`, {
                state: {
                    from: origin ?? kInternalRoutes.courses
                }
            })
        }
    }
}